import React from 'react';
import {
  Box, Button, Chip, Paper, TextField, Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { brandColors, INPUT_HEIGHT_DENSE, RADIUS } from '../../theme/appTheme';
import { toolbarTextFieldSx } from '../../theme/formStyles';
import { useAppTheme } from '../../contexts/ThemeContext';

export interface DashboardDatePreset {
  id: string;
  label: string;
  days: number | null;
}

interface DashboardFilterBarProps {
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  datePresets: DashboardDatePreset[];
  activePresetId: string;
  onPresetClick: (preset: DashboardDatePreset) => void;
  onApply: () => void;
  onReset: () => void;
  loading?: boolean;
  activeFilterCount?: number;
  children?: React.ReactNode;
}

export const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  datePresets,
  activePresetId,
  onPresetClick,
  onApply,
  onReset,
  loading,
  activeFilterCount = 0,
  children,
}) => {
  const { brand } = useAppTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 20,
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        border: `1px solid ${brandColors.border}`,
        bgcolor: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 24px rgba(15,23,42,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <FilterListIcon sx={{ fontSize: 18, color: brand.primary }} />
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: brandColors.textDark }}>
          Filters
        </Typography>
        {activeFilterCount > 0 && (
          <Chip
            label={`${activeFilterCount} active`}
            size="small"
            sx={{ height: 22, fontSize: 11, bgcolor: brand.green100, color: brand.primaryDark, fontWeight: 600 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
          onClick={onReset}
          sx={{ fontSize: 12, minHeight: INPUT_HEIGHT_DENSE, height: INPUT_HEIGHT_DENSE, borderRadius: `${RADIUS}px` }}
        >
          Reset
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onApply}
          disabled={loading}
          sx={{
            fontSize: 12,
            minHeight: INPUT_HEIGHT_DENSE,
            height: INPUT_HEIGHT_DENSE,
            borderRadius: `${RADIUS}px`,
            bgcolor: brand.primary,
            '&:hover': { bgcolor: brand.primaryDark },
          }}
        >
          Apply
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
        {datePresets.map((p) => (
          <Chip
            key={p.id}
            label={p.label}
            size="small"
            clickable
            onClick={() => onPresetClick(p)}
            sx={{
              height: 30,
              fontSize: 12,
              fontWeight: activePresetId === p.id ? 700 : 500,
              borderRadius: '8px',
              bgcolor: activePresetId === p.id ? brand.primary : '#f8fafc',
              color: activePresetId === p.id ? '#fff' : brandColors.textDark,
              border: `1px solid ${activePresetId === p.id ? brand.primary : brandColors.border}`,
              '&:hover': {
                bgcolor: activePresetId === p.id ? brand.primaryDark : brand.green50,
              },
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: children ? 'repeat(4, minmax(0, 1fr))' : '1fr 1fr',
          },
          gap: 1.25,
          alignItems: 'end',
          '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' },
        }}
      >
        <TextField
          label="From"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => onFromDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...toolbarTextFieldSx,
            '& .MuiInputLabel-root': {
              display: 'block',
              position: 'relative',
              transform: 'none',
              mb: '6px',
              fontSize: 13,
              fontWeight: 500,
            },
          }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => onToDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...toolbarTextFieldSx,
            '& .MuiInputLabel-root': {
              display: 'block',
              position: 'relative',
              transform: 'none',
              mb: '6px',
              fontSize: 13,
              fontWeight: 500,
            },
          }}
        />
        {children}
      </Box>
    </Paper>
  );
};
